import { AllStatsDataType } from "./SVGGraphColumnNavTypes";

export type MonthKeyFromDelta = {
  year: number;
  month: number;
  key: string;
};

export const getMonthKeyFromDelta = (
  currentYear: number,
  currentMonth: number,
  delta: number,
): MonthKeyFromDelta => {
  const totalMonths = currentYear * 12 + currentMonth + delta;
  const targetYear = Math.floor(totalMonths / 12);
  const targetMonth = ((totalMonths % 12) + 12) % 12;

  return {
    year: targetYear,
    month: targetMonth,
    key: `${targetYear}-${(targetMonth + 1).toString().padStart(2, "0")}`,
  };
};

export const getCurrentMonthKey = (
  currentYear: number,
  currentMonth: number,
): string => `${currentYear}-${(currentMonth + 1).toString().padStart(2, "0")}`;

export const getMonthKeysWithData = (
  months_data: Record<string, Array<{ value: number; title: string }>> | null,
): string[] => {
  if (!months_data) {
    return [];
  }

  return Object.keys(months_data)
    .filter((key) => (months_data[key]?.length ?? 0) > 0)
    .sort();
};

export const isMonthKeyBefore = (left: string, right: string): boolean =>
  left.localeCompare(right) < 0;

export const isMonthKeyAfter = (left: string, right: string): boolean =>
  left.localeCompare(right) > 0;

export const canNavigateToMonthKey = ({
  monthKey,
  months_data,
  currentYear,
  currentMonth,
}: {
  monthKey: string;
  months_data: Record<string, Array<{ value: number; title: string }>> | null;
  currentYear: number;
  currentMonth: number;
}): boolean => {
  const monthKeysWithData = getMonthKeysWithData(months_data);
  if (monthKeysWithData.length === 0) {
    return false;
  }

  const minMonthKey = monthKeysWithData[0];
  const currentMonthKey = getCurrentMonthKey(currentYear, currentMonth);

  return (
    !isMonthKeyBefore(monthKey, minMonthKey) &&
    !isMonthKeyAfter(monthKey, currentMonthKey)
  );
};

export type NavAvailabilityInput = {
  dataType?: "year" | "month";
  allStatsData?: AllStatsDataType;
  currentYear?: number;
  currentMonth?: number;
  counter?: number;
  deltaCounter?: number;
};

export const getNavAvailability = ({
  dataType,
  allStatsData,
  currentYear,
  currentMonth,
  counter,
  deltaCounter,
}: NavAvailabilityInput): { hasPrevData: boolean; hasNextData: boolean } => {
  if (!allStatsData || !dataType) {
    return { hasPrevData: false, hasNextData: false };
  }

  if (
    dataType === "year" &&
    currentYear !== undefined &&
    counter !== undefined
  ) {
    const currentYearValue = currentYear + (counter || 0);
    const prevYear = currentYearValue - 1;
    const nextYear = currentYearValue + 1;

    return {
      hasPrevData:
        allStatsData.years_data !== null &&
        allStatsData.years_data[prevYear] !== undefined &&
        allStatsData.years_data[prevYear].length > 0,
      hasNextData:
        allStatsData.years_data !== null &&
        allStatsData.years_data[nextYear] !== undefined &&
        allStatsData.years_data[nextYear].length > 0,
    };
  }

  if (
    dataType === "month" &&
    currentYear !== undefined &&
    currentMonth !== undefined &&
    deltaCounter !== undefined
  ) {
    const monthKeysWithData = getMonthKeysWithData(allStatsData.months_data);
    if (monthKeysWithData.length === 0) {
      return { hasPrevData: false, hasNextData: false };
    }

    const minMonthKey = monthKeysWithData[0];
    const currentMonthKey = getCurrentMonthKey(currentYear, currentMonth);
    const prevMonthKey = getMonthKeyFromDelta(
      currentYear,
      currentMonth,
      deltaCounter - 1,
    ).key;
    const nextMonthKey = getMonthKeyFromDelta(
      currentYear,
      currentMonth,
      deltaCounter + 1,
    ).key;

    return {
      hasPrevData: !isMonthKeyBefore(prevMonthKey, minMonthKey),
      hasNextData: !isMonthKeyAfter(nextMonthKey, currentMonthKey),
    };
  }

  return { hasPrevData: false, hasNextData: false };
};
